/** @license
 *
 * Copyright 2024 Esri
 *
 * All rights reserved under the copyright laws of the United States and applicable international laws, treaties,
 * and conventions.
 *
 * This material is licensed for use under the Esri Master License Agreement (MLA), and is bound by the terms of
 * that agreement. You may redistribute and use this code without modification, provided you adhere to the terms
 * of the MLA and include this copyright notice.
 *
 * See use restrictions at http://www.esri.com/legal/pdfs/mla_e204_e300/english
 *
 * For additional information, contact: Environmental Systems Research Institute, Inc. Attn: Contracts and Legal
 * Services Department 380 New York Street Redlands, California, USA 92373 USA
 *
 * email: contracts@esri.com
 */

/**
 * Logger encapsulates the current level of logging to the browser console window.
 *
 * @module Logger
 */

export enum LogLevel {
  None,
  Errors,
  Warnings,
  Info
};

export class Logger {
  private static _instance: Logger;

  /** Level of messages to log to the console window
   *    0: none
   *    1: errors only
   *    2: errors and warnings
   *    3: errors, warnings, and info
   */
  private _consoleMessageLevel: LogLevel;

  /**
   * Constructor; private because this class is a singleton.
   *
   * @param consoleMessageLevel Zero-based level of messages to log to the console window
   */
  private constructor(consoleMessageLevel: LogLevel) {
    this._consoleMessageLevel = consoleMessageLevel;

    // Block `console` from being used by libraries
    switch (consoleMessageLevel) {
      case LogLevel.None:
        console.error = () => { };
        console.warn = () => { };
        console.log = () => { };
        console.info = () => { };
        break;
      case LogLevel.Errors:
        console.warn = () => { };
        console.log = () => { };
        console.info = () => { };
        break;
      case LogLevel.Warnings:
        console.log = () => { };
        console.info = () => { };
        break;
    }
  }

  /**
   * Gets the instance of the logger.
   *
   * @param consoleMessageLevel Zero-based level of messages to log to the console window
   * @returns Instance of the logger
   * @static
   */
  public static getInstance(consoleMessageLevel: LogLevel): Logger {
    if (!Logger._instance) {
      Logger._instance = new Logger(consoleMessageLevel);
    }

    return Logger._instance;
  }

  /**
   * Logs a message to the console window.
   *
   * @param consoleMessageLevel Zero-based level of the message
   * @param message Message to log; can be repeated as needed
   */
  public log(consoleMessageLevel: LogLevel, ...message: any[]): void {
    if (consoleMessageLevel <= this._consoleMessageLevel) {
       
      console.log(...message);
    }
  }

}